import { SupabaseClient } from '@supabase/supabase-js';
import { cache } from 'react';
import { Plan } from '@/types/profile';
import { viddyozeProUserEmails } from '@/utils/viddyozeProUserEmails';
import { Project } from '@/types/project';
import { MediaItem } from '@/types/account-media';

// USER.

export const getUser = cache(async (supabase: SupabaseClient) => {
  const {
    data: { user },
    error: userError,
  } = await supabase.auth.getUser();

  if (userError || !user) {
    // console.error('Error fetching user:', userError);
    return null;
  }

  return user;
});

// USER SUBSCRIPTIONS.

export const getUserSubscriptions = cache(async (supabase: SupabaseClient) => {
  const user = await getUser(supabase);

  if (!user) {
    return [Plan.FREE];
  }

  // Check for pro user emails
  if (user.email && viddyozeProUserEmails.has(user.email)) {
    return [Plan.PRO];
  }

  try {
    // Query subscription overrides
    const { data: subscriptionOverrides, error } = await supabase
      .from('subscription_overrides')
      .select('plan')
      .eq('user_id', user.id);

    if (error) {
      throw error;
    }

    if (subscriptionOverrides && subscriptionOverrides.length > 0) {
      // @ts-ignore
      const plans = subscriptionOverrides.map((sub) => sub.plan as Plan);
      return plans.length > 0 ? plans : [Plan.FREE];
    }

    return [Plan.FREE];
  } catch (e) {
    // console.error('Error fetching user subscriptions:', e);
    return [Plan.FREE];
  }
});

// PROJECTS.

export const getProjectById = cache(
  async (supabase: SupabaseClient, id: string): Promise<Project | null> => {
    const { data, error } = await supabase
      .from('projects')
      .select('*')
      .eq('hashid', id)
      .single();

    if (error) {
      console.error('Error fetching project:', error);
      throw error;
    }

    // If no project is found, return null
    if (!data) {
      return null;
    }

    // Transform the data to match the Project type
    return {
      id: data.hashid,
      ownerId: data.owner_id,
      document: data.document,
      name: data.name,
    };
  }
);

export const getUserProjects = cache(async (supabase: SupabaseClient) => {
  const user = await getUser(supabase);

  if (!user) {
    return null;
  }

  try {
    // Query projects where the user is the owner and join the templates table
    const { data: projects, error: projectsError } = await supabase
      .from('projects')
      .select(
        `created_at, hashid, name, templates ( id, title, published_at, is_premium )`
      )
      .eq('owner_id', user.id)
      .neq('is_deleted', true)
      .throwOnError();

    if (projectsError) {
      throw projectsError;
    }

    return projects.map((project) => ({
      id: project.hashid,
      createdAt: project.created_at,
      name: project.name,
      template: Array.isArray(project.templates)
        ? project.templates[0]
        : project.templates,
    }));
  } catch (e) {
    // console.error('Error fetching user projects:', e);
    return null;
  }
});

// EXPORTS.

export const getExports = cache(async (supabase: SupabaseClient) => {
  const { data, error } = await supabase.from('exports').select();

  if (error) throw error;

  return (data ?? []).map((d) => ({
    assetUrl: d.asset_url,
    createdAt: d.created_at,
    id: d.id,
    jobId: d.job_id,
    jobStatus: d.job_status,
    projectId: d.project_id,
  }));
});

export const getExportsByProjectId = cache(
  async (supabase: SupabaseClient, projectId: string) => {
    const { data, error } = await supabase
      .from('exports')
      .select()
      .eq('project_id', projectId);

    if (error) throw error;

    return (data ?? []).map((d) => ({
      assetUrl: d.asset_url,
      createdAt: d.created_at,
      id: d.id,
      jobId: d.job_id,
      jobStatus: d.job_status,
      bucketName: d.bucket_name,
      projectId: d.project_id,
      retries: d.retries,
    }));
  }
);

export const getExportByJobId = cache(
  async (supabase: SupabaseClient, jobId: string) => {
    const { data, error } = await supabase
      .from('exports')
      .select()
      .eq('job_id', jobId)
      .single();

    if (error) throw error;
    if (!data) throw new Error('Export not found');

    return {
      assetUrl: data.asset_url,
      createdAt: data.created_at,
      id: data.id,
      jobId: data.job_id,
      jobStatus: data.job_status,
    };
  }
);

// MEDIA.

export const getMediaByOwnerId = cache(
  async (supabase: SupabaseClient, ownerId: string): Promise<MediaItem[]> => {
    const user = await getUser(supabase);

    if (!user) {
      return [];
    }

    const { data, error } = await supabase
      .from('account_media')
      .select()
      .eq('owner_id', user.id)
      .throwOnError();

    if (error || !data) {
      console.error('Error fetching account media:', error);
      throw error;
    }

    return data.map((media) => ({
      id: media.id,
      createdAt: media.created_at,
      filename: media.filename ?? '',
      type: media.type ?? 'image',
      source: media.source ?? '',
    }));
  }
);

export const getUserMediaAssets = cache(async (supabase: SupabaseClient) => {
  const {
    data: { user },
    error: userError,
  } = await supabase.auth.getUser();

  if (userError || !user) {
    // console.error('Error fetching user:', userError);
    return null;
  }

  try {
    // Query media assets where the user is the owner
    const { data: mediaAssets, error: mediaAssetsError } = await supabase
      .from('account_media')
      .select('*')
      .eq('owner_id', user.id)
      .throwOnError();

    if (mediaAssetsError) {
      throw mediaAssetsError;
    }

    return mediaAssets.map((media) => ({
      id: media.id,
      createdAt: media.created_at,
      type: media.type ?? 'image',
      source: media.source ?? '',
      filename: media.filename ?? '',
    }));
  } catch (e) {
    // console.error('Error fetching user media assets:', e);
    return null;
  }
});

export const deleteMediaAsset = cache(
  async (supabase: SupabaseClient, id: number) => {
    const { error } = await supabase
      .from('account_media')
      .delete()
      .eq('id', id);

    return error ? false : true;
  }
);

export const createMediaAsset = cache(
  async (
    supabase: SupabaseClient,
    type: 'image' | 'audio' | 'video',
    source: string,
    filename: string
  ): Promise<number | null> => {
    const {
      data: { user },
      error: userError,
    } = await supabase.auth.getUser();

    if (userError || !user) {
      // console.error('Error fetching user:', userError);
      return null;
    }

    try {
      const { data, error } = await supabase
        .from('account_media')
        .insert({
          owner_id: user.id,
          type,
          source,
          filename,
        })
        .select('id')
        .single();

      if (error) {
        throw error;
      }

      return data.id;
    } catch (e) {
      console.error('Error creating media asset:', e);
      return null;
    }
  }
);

// USER PROFILE.

export const getUserProfile = cache(
  async (supabase: SupabaseClient, userId: string) => {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single();

    if (!data) {
      // If no profile is found, insert a new one. Not the best solution!
      const { data: insertedProfile, error: insertError } = await supabase
        .from('profiles')
        .insert({ id: userId })
        .select()
        .single();

      if (insertError) {
        console.error('Error inserting user profile:', insertError);
        return null;
      }

      return insertedProfile;
    }

    if (error) {
      // console.error('Error fetching user profile:', error);
      return null;
    }

    return data;
  }
);

export const updateUserProfile = async (
  supabase: SupabaseClient,
  {
    id,
    usage,
    completed_onboarding,
  }: {
    id: string;
    usage?: string;
    completed_onboarding: boolean;
  }
) => {
  const { data, error } = await supabase
    .from('profiles')
    .upsert({
      id,
      usage,
      completed_onboarding,
    })
    .select()
    .single();

  if (error) {
    console.error('Error updating user profile:', error);
    return null;
  }

  return data;
};

// TEMPLATES.

export const getTemplates = cache(async (supabase: SupabaseClient) => {
  const { data, error } = await supabase.from('templates').select('*');

  if (error) throw error;

  return data.map((template) => ({
    id: template.id,
    title: template.title,
    isPremium: template.is_premium,
    meta: template.meta,
    projectId: template.project_hashid,
  }));
});

export const upsertTemplateById = cache(
  async (supabase: SupabaseClient, id: string, template: any) => {
    const { data, error } = await supabase
      .from('templates')
      .upsert(template)
      .eq('id', id)
      .select();

    return error ? null : data;
  }
);